import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Linkedin, Mail, ArrowUpRight, PlayCircle, Newspaper, Youtube, Radio, Video, Music, BookOpen } from "lucide-react";
import { FOUNDER, TROPHIES, PRESS_MEDIA } from "../data/content";

const Founder = () => {
    return (
        <section id="founder" className="relative py-24 lg:py-32" data-testid="founder">
            <div className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute -left-32 top-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full opacity-[0.06]"
                    style={{
                        background:
                            "radial-gradient(circle, #D4A857 0%, transparent 60%)",
                    }}
                />
            </div>

            <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center lg:items-start">
                    {/* Portrait - sticky on desktop so it floats alongside the bio as you scroll */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 relative lg:sticky lg:top-24 lg:self-start"
                        data-testid="founder-portrait"
                    >
                        <motion.div
                            className="relative"
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        >
                            {/* Gold halo */}
                            <div
                                className="absolute -inset-4 rounded-2xl opacity-60 blur-2xl pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(212,168,87,0.35), transparent 70%)",
                                }}
                            />
                            {/* Frame */}
                            <div
                                className="relative rounded-2xl overflow-hidden"
                                style={{
                                    background:
                                        "linear-gradient(180deg, #1A1A22 0%, #0A0A0C 100%)",
                                    border: "1px solid rgba(212,168,87,0.25)",
                                    boxShadow:
                                        "0 40px 100px -40px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.04)",
                                }}
                            >
                                <img
                                    src={FOUNDER.image}
                                    alt={`${FOUNDER.name} - ${FOUNDER.title}`}
                                    className="block w-full h-auto"
                                    loading="lazy"
                                />
                                {/* Hairline gold corners */}
                                <span className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-gold/50 rounded-tl" />
                                <span className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-gold/50 rounded-br" />
                            </div>
                            {/* Signature plate */}
                            <div
                                className="absolute -bottom-6 left-6 right-6 rounded-xl px-5 py-3 flex items-center justify-between"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #14141A 0%, #0F0F14 100%)",
                                    border: "1px solid rgba(212,168,87,0.3)",
                                    boxShadow: "0 20px 40px -16px rgba(0,0,0,0.6)",
                                }}
                            >
                                <div>
                                    <div className="font-display text-lg text-ivory leading-tight">{FOUNDER.name}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-gold/80 mt-0.5">{FOUNDER.title}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={FOUNDER.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-ivory/70 hover:text-gold hover:border-gold transition-colors"
                                        aria-label={`${FOUNDER.name} on LinkedIn`}
                                        data-testid="founder-linkedin"
                                    >
                                        <Linkedin className="w-4 h-4" />
                                    </a>
                                    <a
                                        href={`mailto:${FOUNDER.email}`}
                                        className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-ivory/70 hover:text-gold hover:border-gold transition-colors"
                                        aria-label="Email"
                                        data-testid="founder-email"
                                    >
                                        <Mail className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Bio */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-7"
                    >
                        <div className="pill"><span className="accent-dot" />Founder</div>
                        <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight leading-[1.02]">
                            {FOUNDER.name}.
                        </h2>
                        <p className="mt-4 font-display italic text-2xl lg:text-3xl text-gold-shimmer leading-snug">
                            {FOUNDER.tagline}
                        </p>

                        <div className="mt-8 space-y-4">
                            {FOUNDER.bio.map((para, i) => (
                                <p key={i} className="text-ivory/75 leading-relaxed">
                                    {para}
                                </p>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-wrap gap-2">
                            {FOUNDER.credentials.map((c) => (
                                <span
                                    key={c}
                                    className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-gold/20 bg-gold/[0.04] text-gold/90"
                                >
                                    {c}
                                </span>
                            ))}
                        </div>

                        {/* Recognition stripe - international awards for RED */}
                        <div
                            className="mt-10 rounded-2xl overflow-hidden"
                            style={{
                                background:
                                    "linear-gradient(120deg, rgba(212,168,87,0.10) 0%, rgba(20,20,26,0.4) 60%, rgba(15,15,20,0.7) 100%)",
                                border: "1px solid rgba(212,168,87,0.22)",
                            }}
                            data-testid="founder-recognition"
                        >
                            <div className="grid grid-cols-2 sm:grid-cols-3 divide-x divide-gold/15">
                                <div className="p-5 lg:p-6">
                                    <div className="font-display text-4xl lg:text-5xl text-gold-shimmer leading-none">202</div>
                                    <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-ivory/55">
                                        International awards<br/>
                                        <span className="text-gold/80">RED - The Color of Silence</span>
                                    </div>
                                </div>
                                <div className="p-5 lg:p-6">
                                    <div className="font-display text-4xl lg:text-5xl text-gold-shimmer leading-none">17×</div>
                                    <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-ivory/55">
                                        Best Actor<br/>
                                        <span className="text-gold/80">International festivals</span>
                                    </div>
                                </div>
                                <div className="col-span-2 sm:col-span-1 p-5 lg:p-6">
                                    <div className="font-display text-4xl lg:text-5xl text-gold-shimmer leading-none">3</div>
                                    <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-ivory/55">
                                        Foundings<br/>
                                        <span className="text-gold/80">IndoBlitz · Pragya Shakti · BPP</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Audience reactions - Facebook video link */}
                        <a
                            href="https://www.facebook.com/share/v/1Lsgac1DEk/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 rounded-2xl flex items-center gap-4 p-5 group transition-all hover:translate-y-[-2px]"
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(212,168,87,0.06) 0%, rgba(20,20,26,0.7) 100%)",
                                border: "1px solid rgba(212,168,87,0.18)",
                            }}
                            data-testid="audience-reactions-cta"
                        >
                            <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 relative overflow-hidden"
                                style={{
                                    background:
                                        "linear-gradient(180deg, #1A1A22, #0F0F14)",
                                    border: "1px solid rgba(212,168,87,0.3)",
                                }}
                            >
                                <PlayCircle className="w-6 h-6 text-gold relative z-10" />
                                <div
                                    className="absolute inset-0 opacity-50 pointer-events-none"
                                    style={{
                                        background:
                                            "radial-gradient(circle at 50% 50%, rgba(212,168,87,0.25), transparent 70%)",
                                    }}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-gold/80 mb-1">
                                    Audience reactions · International screenings
                                </div>
                                <div className="font-display text-lg text-ivory leading-tight">
                                    Watch real audience feedback for <span className="italic text-gold-shimmer">RED.</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gold shrink-0 group-hover:gap-2.5 transition-all">
                                Watch
                                <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </a>

                        {/* Press & Media */}
                        {PRESS_MEDIA.length > 0 && (
                            <div className="mt-8" data-testid="press-media">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Newspaper className="w-4 h-4 text-gold/80" />
                                        <span className="font-mono text-[11px] uppercase tracking-widest text-gold/80">Press &amp; Media</span>
                                    </div>
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-ivory/35">More coming</span>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {PRESS_MEDIA.map((p) => {
                                        const Icon = p.kind === "Article" ? Newspaper
                                            : p.kind === "Radio interview" ? Radio
                                            : p.kind === "Video interview" ? Video
                                            : p.kind === "Original song" ? Music
                                            : p.kind === "Book" ? BookOpen
                                            : Youtube;
                                        return (
                                            <a
                                                key={p.id}
                                                href={p.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="rounded-xl p-4 flex items-start gap-3 group transition-all hover:translate-y-[-2px]"
                                                style={{
                                                    background: "linear-gradient(135deg, rgba(212,168,87,0.05), rgba(20,20,26,0.6))",
                                                    border: "1px solid rgba(212,168,87,0.15)",
                                                }}
                                                data-testid={`press-${p.id}`}
                                            >
                                                <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0">
                                                    <Icon className="w-4 h-4 text-gold" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-mono text-[9px] uppercase tracking-widest text-gold/80 mb-1">
                                                        {p.kind} · {p.outlet}
                                                    </div>
                                                    <div className="text-sm text-ivory leading-snug line-clamp-2 font-medium">{p.title}</div>
                                                    <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-ivory/45">
                                                        {p.date} · {p.location}
                                                    </div>
                                                </div>
                                                <ArrowUpRight className="w-4 h-4 text-ivory/40 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                                            </a>
                                        );
                                    })}
                                </div>
                                <div className="mt-4 text-right">
                                    <Link
                                        to="/community-impact"
                                        className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-gold/85 hover:text-gold hover:gap-2.5 transition-all"
                                        data-testid="view-community-impact"
                                    >
                                        Field dispatches · seminars, school visits &amp; industry talks <ArrowUpRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </div>
                        )}

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <a
                                href={FOUNDER.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-gold !py-2.5 !px-5 text-sm"
                                data-testid="founder-linkedin-cta"
                            >
                                Connect on LinkedIn
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <Link
                                to="/contact"
                                className="btn-ghost !py-2.5 !px-5 text-sm"
                                data-testid="founder-contact-cta"
                            >
                                Reach Bobby directly
                            </Link>
                        </div>
                    </motion.div>
                </div>

                {/* Recognition Wall - 5 trophies on a horizontal band */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-20 lg:mt-24"
                    data-testid="recognition-wall"
                >
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
                        <div>
                            <div className="pill"><span className="accent-dot" />Recognition wall</div>
                            <h3 className="mt-4 font-display text-3xl lg:text-4xl text-ivory tracking-tight">
                                Honoured <span className="italic text-gold-shimmer">across borders.</span>
                            </h3>
                        </div>
                        <p className="text-ivory/55 text-sm max-w-md sm:text-right">
                            A selection from 202 international festival recognitions awarded to RED - The Color of Silence.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        {TROPHIES.map((t, i) => (
                            <motion.figure
                                key={t.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.06 * i }}
                                className="relative rounded-2xl overflow-hidden flex flex-col group glass-hover"
                                style={{
                                    background:
                                        "linear-gradient(180deg, #14141A 0%, #0A0A0C 100%)",
                                    border: "1px solid rgba(212,168,87,0.18)",
                                }}
                                data-testid={`trophy-${t.id}`}
                            >
                                <div className="relative aspect-[3/4] flex items-center justify-center overflow-hidden p-4 bg-gradient-to-br from-[#0F0F14] via-obsidian to-[#070708]">
                                    {/* Spotlight */}
                                    <div
                                        className="absolute inset-0 opacity-40 pointer-events-none"
                                        style={{
                                            background:
                                                "radial-gradient(ellipse 50% 40% at 50% 30%, rgba(212,168,87,0.22), transparent 65%)",
                                        }}
                                    />
                                    <img
                                        src={t.image}
                                        alt={`${t.award} · ${t.festival}`}
                                        className="relative max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                        loading="lazy"
                                        draggable={false}
                                    />
                                    {/* Corner accents */}
                                    <span className="absolute top-2 left-2 w-3 h-3 border-t border-l border-gold/40 rounded-tl" />
                                    <span className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-gold/40 rounded-br" />
                                </div>
                                <figcaption className="p-3 lg:p-4 bg-graphite/40">
                                    <div className="font-display text-sm leading-tight text-ivory">{t.festival}</div>
                                    <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-gold/80">{t.year}</div>
                                    <div className="mt-1.5 text-[11px] text-ivory/55 leading-snug">{t.award}</div>
                                </figcaption>
                                {/* Bottom gold rule on hover */}
                                <div className="absolute left-6 right-6 bottom-[5.5rem] h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </motion.figure>
                        ))}
                    </div>

                    <p className="mt-6 text-xs text-ivory/40 font-mono uppercase tracking-widest text-center">
                        + 157 more international festival recognitions
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default Founder;
